import ky, { HTTPError } from 'ky';
import { newLongId } from '@beamible/uuid';
import ClientError from './utils/ClientError';
import GraphQLError from './utils/GraphQLError';

class Client {
  api = null;

  /**
   * @param {string} host
   * @param {Object} [headers]
   */
  constructor(host, headers = {}, onError) {
    this.api = ky.create({
      timeout: false,
      prefixUrl: host,
      headers,
    });

    this.onError = onError;

    this.isAuthorized = headers?.Authorization?.startsWith('Bearer') ?? false;

    ['get', 'post', 'put', 'patch', 'head', 'delete'].forEach((method) => {
      this[method] = (input, json) => this.request(method, input, json);
    });
  }

  /**
   * @param {object} [options]
   */
  async graphql(options = {}) {
    console.debug('Client.graphql', { query: options.query });
    const response = await this.post('graphql', options);

    if (response.errors) {
      if (this.is404equivalent(response.errors)) {
        return;
      }

      const graphqlError = new GraphQLError(response.errors);

      if (graphqlError.isMutateDesignError()) {
        console.error('Error mutating design', graphqlError.response);
      }
      console.error('graphqlError', graphqlError.response);
      this.onError(graphqlError);
    }

    return response;
  }

  /**
   * @param {string} method
   * @param {string} input
   * @param {Object} payload
   *
   * @throws {ClientError}
   */
  async request(method = 'GET', input, payload) {
    const options = {};

    if (payload instanceof FormData) {
      options.body = payload;
    } else {
      options.json = payload;
    }

    options.headers = {
      ...options.headers,
      'X-Request-ID': newLongId(),
    };

    try {
      const response = await this.api[method](input, options);
      if (response.status === 204) return { status: 204, data: null };
      return await response.json();
    } catch (error) {
      if (error instanceof HTTPError) {
        const data = await error.response.json();
        throw new ClientError(data);
      }

      throw error;
    }
  }

  is404equivalent(errors) {
    const pathsToTreatAs404s = ['design'];

    return (
      pathsToTreatAs404s.includes(errors[0]?.path?.[0] ?? '') &&
      errors[0]?.debugMessage?.includes('No query results')
    );
  }
}

export default Client;
